import { useCallback } from 'react';

import { EmailNotificationSection } from './EmailNotification/EmailNotification.section';

import { ThemeProvider } from '@aircall/tractor-v2';
import { List } from '@components/List/List';
import { Paper } from '@components/Paper/Paper';
import { Loading } from '@dashboard/library';
import {
  GetEmailNotificationsQuery,
  GetEmailNotificationsQueryVariables,
} from '@generated/GetEmailNotificationsQuery';
import { GET_EMAIL_NOTIFICATIONS_QUERY } from '@graphql/queries/GetEmailNotificationsQuery';
import { useGraphQuery } from '@hooks/useQuery';
import { useUpdateEmailNotification } from '@hooks/useUpdateEmailNotification';
import { UserProfileTabProps } from '@pages/user-profile/UserProfile.decl';
import { useTranslation } from 'react-i18next';

export function UserProfileNotifications({ user }: UserProfileTabProps) {
  const { t } = useTranslation();
  const {
    toggleTurnOnEmailNotification,
    toggleTurnOffEmailNotification,
    updateEmailNotificationFrequency,
    isUpdatingEmailNotification,
  } = useUpdateEmailNotification({ user });

  const handleActivationStatusChange = useCallback(
    (name: string, isOff: boolean) => {
      isOff ? toggleTurnOnEmailNotification(name) : toggleTurnOffEmailNotification(name);
    },
    [toggleTurnOnEmailNotification, toggleTurnOffEmailNotification]
  );

  const { data, loading } = useGraphQuery<
    GetEmailNotificationsQuery,
    GetEmailNotificationsQueryVariables
  >(GET_EMAIL_NOTIFICATIONS_QUERY, {
    variables: { agentID: user.ID },
  });

  if (loading) {
    return (
      <ThemeProvider>
        <Loading data-test='email-notifications-loading' />
      </ThemeProvider>
    );
  }

  if (!data?.getEmailNotifications) {
    return null;
  }

  return (
    <Paper
      title={t('user_profile.notifications.email_notifications.title')}
      data-test='user-profile-notifications'
    >
      <List>
        {data.getEmailNotifications.map(({ name, frequencies, defaultFrequency }) => {
          const userNotification = user.emailNotifications?.find(
            ({ name: enName }) => enName === name
          );

          return (
            <EmailNotificationSection
              name={name}
              key={name}
              data-test={`email-notification-${name}`}
              frequencyOptions={frequencies}
              frequency={userNotification?.frequency ?? defaultFrequency}
              isOn={!!userNotification}
              isUpdating={isUpdatingEmailNotification}
              onActivationStatusChange={handleActivationStatusChange}
              onFrequencyChange={updateEmailNotificationFrequency}
            />
          );
        })}
      </List>
    </Paper>
  );
}
